import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { performanceAPI } from '../utils/supabase'

// Fixed 14-day window for all analytics
const TIME_WINDOW = 14

// Async thunks
export const fetchPerformanceData = createAsyncThunk(
  'performance/fetch',
  async ({ userId }, { rejectWithValue, getState }) => {
    try {
      // Get user settings from Redux state
      const userSettings = getState().userSettings.settings || null
      const userTimezone = userSettings?.timezone || null

      const [
        velocityResult,
        summaryResult,
        burndownResult,
        cycleTimeResult,
        productivityResult
      ] = await Promise.all([
        performanceAPI.getDailyVelocity(userId, TIME_WINDOW, userSettings),
        performanceAPI.getVelocitySummary(userId, TIME_WINDOW),
        performanceAPI.getDailyBurndown(userId, TIME_WINDOW),
        performanceAPI.getCycleTimeAnalysis(userId, TIME_WINDOW, userTimezone, userSettings),
        performanceAPI.getProductivityPatterns(userId, TIME_WINDOW, userTimezone)
      ])
      
      if (velocityResult.error) throw velocityResult.error
      if (summaryResult.error) throw summaryResult.error
      if (burndownResult.error) throw burndownResult.error
      if (cycleTimeResult.error) throw cycleTimeResult.error
      if (productivityResult.error) throw productivityResult.error
      
      return {
        velocityData: velocityResult.data || [],
        summaryData: summaryResult.data?.[0] || null,
        burndownData: burndownResult.data || [],
        cycleTimeData: cycleTimeResult.data || [],
        productivityData: productivityResult.data || []
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const refreshPerformanceData = createAsyncThunk(
  'performance/refresh',
  async ({ userId }, { rejectWithValue, getState }) => {
    try {
      // Get user settings from Redux state
      const userSettings = getState().userSettings.settings || null
      const userTimezone = userSettings?.timezone || null

      const [
        velocityResult,
        summaryResult,
        burndownResult,
        cycleTimeResult,
        productivityResult
      ] = await Promise.all([
        performanceAPI.getDailyVelocity(userId, TIME_WINDOW, userSettings),
        performanceAPI.getVelocitySummary(userId, TIME_WINDOW),
        performanceAPI.getDailyBurndown(userId, TIME_WINDOW),
        performanceAPI.getCycleTimeAnalysis(userId, TIME_WINDOW, userTimezone, userSettings),
        performanceAPI.getProductivityPatterns(userId, TIME_WINDOW, userTimezone)
      ])
      
      if (velocityResult.error) throw velocityResult.error
      if (summaryResult.error) throw summaryResult.error
      if (burndownResult.error) throw burndownResult.error
      if (cycleTimeResult.error) throw cycleTimeResult.error
      if (productivityResult.error) throw productivityResult.error
      
      return {
        velocityData: velocityResult.data || [],
        summaryData: summaryResult.data?.[0] || null,
        burndownData: burndownResult.data || [],
        cycleTimeData: cycleTimeResult.data || [],
        productivityData: productivityResult.data || []
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  velocityData: [],
  summaryData: null,
  burndownData: [],
  cycleTimeData: [],
  productivityData: [],
  loading: false,
  error: null,
  initialized: false,
  lastRefresh: null
}

const performanceSlice = createSlice({
  name: 'performance',
  initialState,
  reducers: {
    clearPerformanceData: (state) => {
      state.velocityData = []
      state.summaryData = null
      state.burndownData = []
      state.cycleTimeData = []
      state.productivityData = []
      state.initialized = false
      state.error = null
      state.lastRefresh = null
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Performance Data
      .addCase(fetchPerformanceData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPerformanceData.fulfilled, (state, action) => {
        state.loading = false
        state.velocityData = action.payload.velocityData
        state.summaryData = action.payload.summaryData
        state.burndownData = action.payload.burndownData
        state.cycleTimeData = action.payload.cycleTimeData
        state.productivityData = action.payload.productivityData
        state.initialized = true
        state.lastRefresh = new Date().toISOString()
      })
      .addCase(fetchPerformanceData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = true
      })

      // Refresh Performance Data
      .addCase(refreshPerformanceData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshPerformanceData.fulfilled, (state, action) => {
        state.loading = false
        state.velocityData = action.payload.velocityData
        state.summaryData = action.payload.summaryData
        state.burndownData = action.payload.burndownData
        state.cycleTimeData = action.payload.cycleTimeData
        state.productivityData = action.payload.productivityData
        state.lastRefresh = new Date().toISOString()
      })
      .addCase(refreshPerformanceData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearPerformanceData, clearError } = performanceSlice.actions

// Selectors
export const selectPerformanceData = (state) => state.performance
export const selectVelocityData = (state) => state.performance.velocityData
export const selectSummaryData = (state) => state.performance.summaryData
export const selectBurndownData = (state) => state.performance.burndownData
export const selectCycleTimeData = (state) => state.performance.cycleTimeData
export const selectProductivityData = (state) => state.performance.productivityData
export const selectPerformanceLoading = (state) => state.performance.loading
export const selectPerformanceError = (state) => state.performance.error
export const selectPerformanceInitialized = (state) => state.performance.initialized
export const selectLastRefresh = (state) => state.performance.lastRefresh

export default performanceSlice.reducer
